import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {useIsInitialLocation} from '@aboutyou/router';
import {useEffect, useState} from 'react';
import {ProductTileTrackedProps} from '../ProductTileTracked';

const PRODUCT_TILE_TRACKING_DATA = 'PRODUCT_TILE_TRACKING_DATA';
const PRODUCT_TILE_TRACKING_DATA_RECEIVE = 'PRODUCT_TILE_TRACKING_DATA_RECEIVE';

interface Payload {
  productId?: number;
  trackingData: ProductTileTrackedProps['trackingData'];
}

export const useReceivedTrackingData = ({productId}: {productId?: number}) => {
  const [trackingData, setTrackingData] = useState<
    Payload['trackingData'] | undefined
  >();
  const [isTrackingBlocked, setIsTrackingBlocked] = useState(true);
  const isInitialLocation = useIsInitialLocation();
  const {isActive: isNewTabActive} = useIsFeatureActive('ab_adp_new_tab');

  useEffect(() => {
    const fallback = setTimeout(() => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setIsTrackingBlocked(false);
    }, 1000);

    return () => {
      clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    if (trackingData || !isNewTabActive) {
      return;
    }
    const dataBroadcastChannel = new BroadcastChannel(
      PRODUCT_TILE_TRACKING_DATA,
    );
    const receiveBroadcastChannel = new BroadcastChannel(
      PRODUCT_TILE_TRACKING_DATA_RECEIVE,
    );

    receiveBroadcastChannel.onmessage = event => {
      if (event.data.productId === productId) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setTrackingData(event.data.trackingData);
        setIsTrackingBlocked(false);
      }
    };
    dataBroadcastChannel.postMessage({ready: true});

    return () => {
      receiveBroadcastChannel.close();
      dataBroadcastChannel.close();
    };
  }, [productId, trackingData]);

  return {
    trackingData,
    isTrackingBlocked:
      isInitialLocation && !trackingData && isNewTabActive && isTrackingBlocked,
  };
};

export const useTileTrackingData = () => {
  const [dataToSend, setDataToSend] = useState<
    | {
        trackingData: ProductTileTrackedProps['trackingData'] | undefined;
        productId?: number;
      }
    | undefined
  >();

  useEffect(() => {
    if (!dataToSend) {
      return;
    }
    const receiveBroadcastChannel = new BroadcastChannel(
      PRODUCT_TILE_TRACKING_DATA_RECEIVE,
    );
    const dataBroadcastChannel = new BroadcastChannel(
      PRODUCT_TILE_TRACKING_DATA,
    );

    dataBroadcastChannel.onmessage = event => {
      if (event.data.ready) {
        receiveBroadcastChannel.postMessage({
          productId: dataToSend.productId,
          trackingData: dataToSend.trackingData,
        });
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setDataToSend(undefined);
      }
    };

    return () => {
      receiveBroadcastChannel.close();
      dataBroadcastChannel.close();
    };
  }, [dataToSend]);

  const sendTrackingData = (
    trackingData: ProductTileTrackedProps['trackingData'],
    productId?: number,
  ) => {
    setDataToSend({trackingData, productId});
  };

  return {sendTrackingData};
};
